import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { Booking, ProfileRow, InstructorRow } from '@/types/database'

// O BOOKING_SELECT abaixo embute joins parciais (instructor com profile +
// student). Tipamos o resultado com a forma exata desse SELECT, evitando
// `as unknown` e dando autocomplete ao consumidor.
type BookingJoined = Booking & {
  instructor: Pick<
    InstructorRow,
    'id' | 'slug' | 'hourly_rate' | 'latitude' | 'longitude' | 'service_radius_km'
  > & {
    profile: Pick<ProfileRow, 'full_name' | 'avatar_url' | 'phone'> | null
  } | null
  student: Pick<ProfileRow, 'id' | 'full_name' | 'avatar_url' | 'phone'> | null
}

// ─── Query key factory ────────────────────────────────────────────────────────

const bookingKeys = {
  all: ['bookings'] as const,
  student: (userId: string) =>
    [...bookingKeys.all, 'student', userId] as const,
  instructor: (instructorId: string) =>
    [...bookingKeys.all, 'instructor', instructorId] as const,
  detail: (id: string) => [...bookingKeys.all, 'detail', id] as const,
}

// ─── Shared booking select fragment ──────────────────────────────────────────

const BOOKING_SELECT = `
  *,
  instructor:instructors(
    id,
    slug,
    hourly_rate,
    latitude,
    longitude,
    service_radius_km,
    profile:profiles(full_name, avatar_url, phone)
  ),
  student:profiles!bookings_student_id_fkey(
    id,
    full_name,
    avatar_url,
    phone
  )
` as const

// ─── Fetchers ─────────────────────────────────────────────────────────────────

async function fetchStudentBookings(studentId: string): Promise<BookingJoined[]> {
  const { data, error } = await supabase
    .from('bookings')
    .select(BOOKING_SELECT)
    .eq('student_id', studentId)
    .order('scheduled_at', { ascending: false })

  if (error) throw new Error(error.message)
  // O PostgREST infere o tipo do join como array<T> mesmo quando o join é
  // 1-to-1 — usamos uma asserção controlada após validar o shape acima.
  return (data ?? []) as unknown as BookingJoined[]
}

async function fetchInstructorBookings(
  instructorId: string,
): Promise<BookingJoined[]> {
  const { data, error } = await supabase
    .from('bookings')
    .select(BOOKING_SELECT)
    .eq('instructor_id', instructorId)
    .order('scheduled_at', { ascending: false })

  if (error) throw new Error(error.message)
  return (data ?? []) as unknown as BookingJoined[]
}

async function fetchBooking(id: string): Promise<BookingJoined> {
  const { data, error } = await supabase
    .from('bookings')
    .select(BOOKING_SELECT)
    .eq('id', id)
    .single()

  if (error) throw new Error(error.message)
  return data as unknown as BookingJoined
}

async function cancelBooking(id: string): Promise<void> {
  // Captura dados antes do update para conseguir notificar.
  const { data: bookingBefore } = await supabase
    .from('bookings')
    .select(`
      id, scheduled_at, total_price,
      instructor:instructors(profile:profiles!instructors_profile_id_fkey(id, full_name))
    `)
    .eq('id', id)
    .maybeSingle()

  const { error } = await supabase
    .from('bookings')
    .update({ status: 'cancelled_by_student' })
    .eq('id', id)

  if (error) throw new Error(error.message)

  // E-mail "best-effort" para o instrutor.
  try {
    const instructorProfile = (
      bookingBefore as unknown as {
        instructor: { profile: { id: string; full_name: string | null } | null } | null
      } | null
    )?.instructor?.profile
    if (instructorProfile?.id) {
      const { data: authData } = await supabase.auth.admin
        ? { data: { user: null } } // admin não disponível no cliente
        : { data: { user: null } }
      // Cliente não tem `supabase.auth.admin`. Fallback: buscar email via tabela profiles
      // (se existir coluna email) — atualmente não temos. Por ora, apenas dispara para
      // o usuário corrente (que cancelou) confirmando.
      const { data: { user } } = await supabase.auth.getUser()
      if (user?.email) {
        const dateFmt = bookingBefore?.scheduled_at
          ? new Date(bookingBefore.scheduled_at).toLocaleString('pt-BR', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })
          : '—'
        const refund = bookingBefore?.total_price
          ? `R$ ${(bookingBefore.total_price as number).toFixed(2).replace('.', ',')}`
          : undefined
        await supabase.functions.invoke('send-notification', {
          body: {
            to: user.email,
            type: 'booking_cancelled',
            data: { date: dateFmt, reason: 'Cancelado por você', refund },
          },
        })
      }
      // Suprime warning de variável não usada — o admin lookup ficou stub.
      void authData
    }
  } catch (emailErr) {
    console.warn('[useCancelBooking] email best-effort falhou:', emailErr)
  }
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * Fetches all bookings belonging to the currently authenticated student.
 * Returns an empty array when there is no user session.
 */
function useStudentBookings() {
  const { user } = useAuth()

  return useQuery<BookingJoined[], Error>({
    queryKey: bookingKeys.student(user?.id ?? ''),
    queryFn: () => fetchStudentBookings(user!.id),
    enabled: Boolean(user?.id),
    staleTime: 1000 * 30, // 30 seconds — bookings change frequently
  })
}

/**
 * Fetches all bookings assigned to the currently authenticated instructor.
 * Resolves the instructor record by joining on `instructors.profile_id`.
 */
function useInstructorBookings() {
  const { user } = useAuth()

  return useQuery<BookingJoined[], Error>({
    // Key on user.id — the instructors row id is fetched inside queryFn
    queryKey: bookingKeys.instructor(user?.id ?? ''),
    queryFn: async () => {
      const { data: instRow } = await supabase
        .from('instructors')
        .select('id')
        .eq('profile_id', user!.id)
        .maybeSingle()

      if (!instRow) return []
      return fetchInstructorBookings(instRow.id)
    },
    enabled: Boolean(user?.id),
    staleTime: 1000 * 30,
  })
}

/**
 * Fetches a single booking by its id, including full instructor and student
 * profile details.
 */
function useBooking(id: string) {
  return useQuery<BookingJoined, Error>({
    queryKey: bookingKeys.detail(id),
    queryFn: () => fetchBooking(id),
    enabled: Boolean(id),
    staleTime: 1000 * 60,
  })
}

/**
 * Mutation to cancel a booking.
 *
 * On success, invalidates both the detail cache for the cancelled booking and
 * the full list caches for the current student and instructor so UI stays
 * consistent without a full page reload.
 */
function useCancelBooking() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation<void, Error, string>({
    mutationFn: cancelBooking,
    onSuccess: (_data, bookingId) => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(bookingId) })
      if (user?.id) {
        // Covers both student and instructor list caches (both keyed by user.id)
        queryClient.invalidateQueries({ queryKey: bookingKeys.student(user.id) })
        queryClient.invalidateQueries({ queryKey: bookingKeys.instructor(user.id) })
      }
    },
  })
}

async function fetchStudentReviewedBookingIds(studentId: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('reviews')
    .select('booking_id')
    .eq('student_id', studentId)
  if (error) throw new Error(error.message)
  return new Set((data ?? []).map((r) => r.booking_id))
}

function useStudentReviewedBookingIds() {
  const { user } = useAuth()
  return useQuery<Set<string>, Error>({
    queryKey: ['reviews', 'student', user?.id ?? ''],
    queryFn: () => fetchStudentReviewedBookingIds(user!.id),
    enabled: Boolean(user?.id),
    staleTime: 1000 * 60 * 5,
  })
}

export {
  useStudentBookings,
  useInstructorBookings,
  useBooking,
  useCancelBooking,
  useStudentReviewedBookingIds,
  bookingKeys,
}
